import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  IconButton,
  TextField,
  Slider,
  Divider,
  Chip,
  CircularProgress,
  LinearProgress
} from '@mui/material'
import {
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material'
import { Cigar, useMoveCigarMutation } from '@store/api/cigarApi'
import { useGetHumidorsQuery, Humidor } from '@store/api/humidorApi'

interface MoveCigarDialogProps {
  open: boolean
  onClose: () => void
  cigar: Cigar | null
}

const MoveCigarDialog: React.FC<MoveCigarDialogProps> = ({ open, onClose, cigar }) => {
  const [destinationHumidorId, setDestinationHumidorId] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [error, setError] = useState('')
  const [step, setStep] = useState<'select' | 'preview' | 'success'>('select')

  const { data: humidors = [] } = useGetHumidorsQuery()
  const [moveCigar, { isLoading }] = useMoveCigarMutation()

  const handleClose = () => {
    setDestinationHumidorId('')
    setQuantity(1)
    setError('')
    setStep('select')
    onClose()
  }

  const handleOpen = () => {
    if (cigar) {
      setQuantity(cigar.quantity)
    }
  }

  const handlePreview = () => {
    if (!destinationHumidorId) return
    setError('')
    setStep('preview')
  }

  const handleBack = () => {
    setStep('select')
    setError('')
  }

  const handleConfirm = async () => {
    if (!cigar || !destinationHumidorId) return
    setError('')

    try {
      await moveCigar({ id: cigar.id, destinationHumidorId, quantity }).unwrap()
      setStep('success')
      setTimeout(() => {
        handleClose()
      }, 1500)
    } catch (err: any) {
      const msg = err?.data?.message || 'Failed to move cigar. Please try again.'
      const available = err?.data?.availableQuantity
      setError(available ? `${msg} Available: ${available}` : msg)
      setStep('select')
    }
  }

  if (!cigar) return null

  const cigarName = `${cigar.brand?.name || 'Unknown'} - ${cigar.cigarLine?.name || 'Unknown'}`
  const availableHumidors = (Array.isArray(humidors) ? humidors : []).filter(
    (h: any) => h.id !== cigar.humidorId && !h.isArchived
  )

  const sourceHumidor = (Array.isArray(humidors) ? humidors : []).find(
    (h: any) => h.id === cigar.humidorId
  )

  const destHumidor = availableHumidors.find((h: any) => h.id === destinationHumidorId) as Humidor | undefined

  const destCurrentCount = destHumidor?._count?.cigars ?? destHumidor?.cigarCount ?? 0
  const destCapacity = destHumidor?.capacity ?? 0
  const destAvailable = destCapacity > 0 ? destCapacity - destCurrentCount : null
  const destFull = destCapacity > 0 && destCurrentCount >= destCapacity
  const destUsagePercent = destCapacity > 0 ? (destCurrentCount / destCapacity) * 100 : 0

  return (
    <Dialog open={open} onClose={handleClose} onTransitionEnter={handleOpen} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Move Cigar</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          <strong>{cigarName}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Currently in: <strong>{cigar.humidor?.name || 'Unknown'}</strong> &bull; Available: {cigar.quantity}
        </Typography>

        {step === 'select' && (
          <>
            <FormControl fullWidth sx={{ mt: 3 }} required>
              <InputLabel>Destination Humidor</InputLabel>
              <Select
                value={destinationHumidorId}
                label="Destination Humidor"
                onChange={(e) => setDestinationHumidorId(e.target.value)}
              >
                {availableHumidors.map((h: any) => {
                  const count = h._count?.cigars ?? h.cigarCount ?? 0
                  const isFull = h.capacity > 0 && count >= h.capacity
                  return (
                    <MenuItem key={h.id} value={h.id} disabled={isFull}>
                      {h.name} ({count}/{h.capacity}){isFull ? ' — Full' : ''}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            {cigar.quantity > 1 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Quantity to move: <strong>{quantity}</strong> of {cigar.quantity}
                </Typography>
                <Slider
                  value={quantity}
                  min={1}
                  max={cigar.quantity}
                  step={1}
                  marks
                  valueLabelDisplay="auto"
                  onChange={(_e, val) => setQuantity(val as number)}
                />
                <Box display="flex" gap={2} mt={1}>
                  <TextField
                    label="Quantity"
                    type="number"
                    size="small"
                    value={quantity}
                    inputProps={{ min: 1, max: cigar.quantity }}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10)
                      if (!isNaN(val) && val >= 1 && val <= cigar.quantity) {
                        setQuantity(val)
                      }
                    }}
                    sx={{ width: 100 }}
                  />
                </Box>
              </Box>
            )}

            {availableHumidors.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No other humidors available. Create another humidor first.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {step === 'preview' && destHumidor && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Movement Preview
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ mt: 1 }}>
              <Box flex={1} p={1.5} bgcolor="background.default" borderRadius={1} textAlign="center">
                <Typography variant="caption" color="text.secondary" display="block">From</Typography>
                <Typography variant="body2" fontWeight="bold">{sourceHumidor?.name ?? cigar.humidor?.name}</Typography>
              </Box>
              <ArrowIcon color="action" />
              <Box flex={1} p={1.5} bgcolor="background.default" borderRadius={1} textAlign="center">
                <Typography variant="caption" color="text.secondary" display="block">To</Typography>
                <Typography variant="body2" fontWeight="bold">{destHumidor.name}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Destination Capacity</Typography>
            <Box sx={{ mb: 1 }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">{destCurrentCount} / {destCapacity} cigars</Typography>
                {destAvailable !== null && (
                  <Chip
                    size="small"
                    label={`${destAvailable} available`}
                    color={destFull ? 'error' : destAvailable <= 5 ? 'warning' : 'success'}
                  />
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(destUsagePercent, 100)}
                color={destFull ? 'error' : destUsagePercent > 80 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>

            {destFull && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Destination humidor is at full capacity.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {step === 'success' && (
          <Box display="flex" flexDirection="column" alignItems="center" py={2} gap={1}>
            <CheckIcon color="success" sx={{ fontSize: 48 }} />
            <Typography variant="body1" color="success.main">
              Cigar moved successfully!
            </Typography>
          </Box>
        )}
      </DialogContent>

      {step !== 'success' && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          {step === 'preview' ? (
            <>
              <Button onClick={handleBack} color="inherit" disabled={isLoading}>
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                disabled={isLoading || destFull}
                startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
              >
                {isLoading ? 'Moving...' : `Move ${quantity > 1 ? `${quantity} ` : ''}Cigar${quantity > 1 ? 's' : ''}`}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                variant="contained"
                disabled={!destinationHumidorId}
              >
                Preview Move
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default MoveCigarDialog
